import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Gift, CalendarPlus, Coins, Ban, Search, Infinity as InfinityIcon } from "lucide-react";
import { adminListUsersWithSubscriptions, adminGrantCredits, adminGiftSubscription, adminExtendSubscription, adminCancelSubscription } from "../../../lib/api";
import { ActionDialog, fmtDate } from "./ActionDialog";

export function UserSubscriptionsPanel({ tiers }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [busyUser, setBusyUser] = useState("");
    // action: { kind: "grant"|"gift"|"extend"|"cancel", user }
    const [action, setAction] = useState(null);
    // form scratch
    const [credits, setCredits] = useState(100);
    const [creditNote, setCreditNote] = useState("");
    const [pickedTier, setPickedTier] = useState("");
    const [days, setDays] = useState(30);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const list = await adminListUsersWithSubscriptions();
            setUsers(list);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't load users.",
            );
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        refresh();
    }, [refresh]);

    const openAction = (kind, u) => {
        setAction({ kind, user: u });
        if (kind === "grant") {
            setCredits(100);
            setCreditNote("");
        } else if (kind === "gift") {
            setPickedTier(tiers[0]?.id || "");
            setDays(30);
            setCreditNote("");
        } else if (kind === "extend") {
            setDays(30);
        }
    };

    const closeAction = () => setAction(null);

    const handleConfirm = async () => {
        if (!action) return;
        const uid = action.user.user_id;
        setBusyUser(uid);
        try {
            if (action.kind === "grant") {
                if (!credits || Number.isNaN(Number(credits))) {
                    toast.error("Enter a non-zero amount.");
                    setBusyUser("");
                    return;
                }
                await adminGrantCredits(uid, Number(credits), creditNote);
                toast.success(
                    `${Number(credits) >= 0 ? "Added" : "Removed"} ${Math.abs(
                        Number(credits),
                    )} credits.`,
                );
            } else if (action.kind === "gift") {
                if (!pickedTier) {
                    toast.error("Pick a tier first.");
                    setBusyUser("");
                    return;
                }
                await adminGiftSubscription(uid, pickedTier, days, creditNote);
                toast.success("Subscription granted.");
            } else if (action.kind === "extend") {
                await adminExtendSubscription(uid, days);
                toast.success(`Extended by ${days} days.`);
            } else if (action.kind === "cancel") {
                await adminCancelSubscription(uid);
                toast.success("Subscription cancelled.");
            }
            closeAction();
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed.");
        } finally {
            setBusyUser("");
        }
    };

    const filtered = useMemo(() => {
        if (!query.trim()) return users;
        const q = query.toLowerCase();
        return users.filter(
            (u) =>
                (u.email || "").toLowerCase().includes(q) ||
                (u.full_name || "").toLowerCase().includes(q) ||
                (u.balance?.tier_title || "").toLowerCase().includes(q),
        );
    }, [users, query]);

    return (
        <section
            className="bg-[#131A2F]/80 border border-[#2A3655] rounded-md overflow-hidden"
            data-testid="user-subscriptions-panel"
        >
            <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#2A3655] bg-[#1C253C]">
                <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#00A3FF]" />
                    User subscriptions
                </h2>
                <div className="relative w-72 max-w-full">
                    <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search email / name / tier…"
                        data-testid="user-sub-search"
                        className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm pl-8 pr-3 py-1.5 text-xs text-white outline-none"
                    />
                </div>
            </header>
            <div className="overflow-x-auto">
                <table
                    className="w-full text-sm"
                    data-testid="user-sub-table"
                >
                    <thead>
                        <tr className="bg-[#0A1128] border-b border-[#2A3655] text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                            <th className="px-3 py-2 text-left">User</th>
                            <th className="px-3 py-2 text-left">Tier</th>
                            <th className="px-3 py-2 text-right">Credits</th>
                            <th className="px-3 py-2 text-left">Period ends</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan="5"
                                    className="px-3 py-6 text-center text-slate-500"
                                >
                                    Loading…
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td
                                    colSpan="5"
                                    className="px-3 py-6 text-center text-slate-500"
                                >
                                    No users.
                                </td>
                            </tr>
                        ) : (
                            filtered.map((u) => {
                                const b = u.balance || {};
                                const unlimited = !!b.unlimited;
                                const isSA = u.role === "superadmin";
                                return (
                                    <tr
                                        key={u.user_id}
                                        className="border-b border-[#1C253C] hover:bg-[#0F1B3A]/60"
                                        data-testid={`user-sub-row-${u.user_id}`}
                                    >
                                        <td className="px-3 py-2.5">
                                            <div className="text-white font-semibold">
                                                {u.full_name || u.email || u.user_id}
                                            </div>
                                            <div className="text-[10px] text-slate-500">
                                                {u.email}
                                                {isSA && (
                                                    <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400 font-bold">
                                                        superadmin
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 text-slate-300">
                                            {b.tier_title || (
                                                <span className="text-slate-600 italic">
                                                    no plan
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums">
                                            {unlimited ? (
                                                <span className="inline-flex items-center gap-1 text-amber-400 font-bold">
                                                    <InfinityIcon className="w-3.5 h-3.5" />
                                                    Unlimited
                                                </span>
                                            ) : b.has_subscription ? (
                                                <span className="text-[#00A3FF] font-semibold">
                                                    {Number(b.credits_remaining).toLocaleString()}
                                                    <span className="text-slate-500 font-normal">
                                                        {" "}
                                                        / {Number(b.credit_amount).toLocaleString()}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="text-slate-600">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-slate-400">
                                            {unlimited
                                                ? "—"
                                                : fmtDate(b.period_end)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                            {isSA ? (
                                                <span className="text-[10px] text-slate-500">
                                                    bypasses billing
                                                </span>
                                            ) : (
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => openAction("grant", u)}
                                                        disabled={busyUser === u.user_id}
                                                        data-testid={`user-sub-grant-${u.user_id}`}
                                                        title="Add or remove credits"
                                                        className="w-7 h-7 rounded-sm flex items-center justify-center text-emerald-400 hover:bg-emerald-500/10"
                                                    >
                                                        <Coins className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openAction("gift", u)}
                                                        disabled={busyUser === u.user_id}
                                                        data-testid={`user-sub-gift-${u.user_id}`}
                                                        title="Gift / change subscription"
                                                        className="w-7 h-7 rounded-sm flex items-center justify-center text-[#00A3FF] hover:bg-[#007AFF]/10"
                                                    >
                                                        <Gift className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openAction("extend", u)}
                                                        disabled={
                                                            busyUser === u.user_id ||
                                                            !b.has_subscription
                                                        }
                                                        data-testid={`user-sub-extend-${u.user_id}`}
                                                        title="Extend period"
                                                        className="w-7 h-7 rounded-sm flex items-center justify-center text-amber-400 hover:bg-amber-500/10 disabled:opacity-30 disabled:hover:bg-transparent"
                                                    >
                                                        <CalendarPlus className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openAction("cancel", u)}
                                                        disabled={
                                                            busyUser === u.user_id ||
                                                            !b.has_subscription
                                                        }
                                                        data-testid={`user-sub-cancel-${u.user_id}`}
                                                        title="Cancel subscription"
                                                        className="w-7 h-7 rounded-sm flex items-center justify-center text-rose-400 hover:bg-rose-500/10 disabled:opacity-30 disabled:hover:bg-transparent"
                                                    >
                                                        <Ban className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Grant credits dialog */}
            <ActionDialog
                open={action?.kind === "grant"}
                title={`Adjust credits — ${action?.user?.email || ""}`}
                onClose={closeAction}
                onConfirm={handleConfirm}
                busy={!!busyUser}
                confirmLabel="Apply"
                confirmIcon={Coins}
                confirmTone="green"
            >
                <p className="text-xs text-slate-400">
                    Positive number adds credits, negative removes. Gifted
                    credits bypass payment and sit alongside the user's
                    normal monthly allotment.
                </p>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                        Credits
                    </span>
                    <input
                        type="number"
                        step="1"
                        value={credits}
                        onChange={(e) => setCredits(e.target.value)}
                        data-testid="user-sub-grant-input"
                        className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none tabular-nums"
                    />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                        Note (optional)
                    </span>
                    <input
                        type="text"
                        value={creditNote}
                        onChange={(e) => setCreditNote(e.target.value)}
                        placeholder="e.g. Beta thank-you"
                        data-testid="user-sub-grant-note"
                        className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none"
                    />
                </label>
            </ActionDialog>

            {/* Gift subscription dialog */}
            <ActionDialog
                open={action?.kind === "gift"}
                title={`Gift subscription — ${action?.user?.email || ""}`}
                onClose={closeAction}
                onConfirm={handleConfirm}
                busy={!!busyUser}
                confirmLabel="Grant subscription"
                confirmIcon={Gift}
            >
                <p className="text-xs text-slate-400">
                    Bypasses RevenueCat / payment. Starts a fresh period
                    with the tier's full credit allotment.
                </p>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                        Tier
                    </span>
                    <select
                        value={pickedTier}
                        onChange={(e) => setPickedTier(e.target.value)}
                        data-testid="user-sub-gift-tier"
                        className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none"
                    >
                        {tiers.length === 0 && (
                            <option value="">
                                No tiers exist — create one first.
                            </option>
                        )}
                        {tiers.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.title} — ${Number(t.price_usd).toFixed(2)} /{" "}
                                {Number(t.credit_amount).toLocaleString()} cr /{" "}
                                {t.duration_days}d
                            </option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                        Override duration (days)
                    </span>
                    <input
                        type="number"
                        min="1"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value) || 30)}
                        data-testid="user-sub-gift-days"
                        className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none tabular-nums"
                    />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                        Note (optional)
                    </span>
                    <input
                        type="text"
                        value={creditNote}
                        onChange={(e) => setCreditNote(e.target.value)}
                        placeholder="e.g. Pilot customer"
                        data-testid="user-sub-gift-note"
                        className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none"
                    />
                </label>
            </ActionDialog>

            {/* Extend period dialog */}
            <ActionDialog
                open={action?.kind === "extend"}
                title={`Extend period — ${action?.user?.email || ""}`}
                onClose={closeAction}
                onConfirm={handleConfirm}
                busy={!!busyUser}
                confirmLabel="Extend"
                confirmIcon={CalendarPlus}
            >
                <p className="text-xs text-slate-400">
                    Adds the given number of days to the user's
                    <span className="font-semibold text-white">
                        {" "}
                        current period_end
                    </span>{" "}
                    (or to "now" if already lapsed). Credits don't reset
                    — only the renewal date moves.
                </p>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                        Days to add
                    </span>
                    <input
                        type="number"
                        min="1"
                        max="3650"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value) || 30)}
                        data-testid="user-sub-extend-days"
                        className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none tabular-nums"
                    />
                </label>
            </ActionDialog>

            {/* Cancel confirmation */}
            <ActionDialog
                open={action?.kind === "cancel"}
                title={`Cancel subscription — ${action?.user?.email || ""}`}
                onClose={closeAction}
                onConfirm={handleConfirm}
                busy={!!busyUser}
                confirmLabel="Cancel subscription"
                confirmIcon={Ban}
                confirmTone="red"
            >
                <p className="text-sm text-slate-300">
                    Mark this user's subscription as{" "}
                    <span className="font-bold text-rose-300">expired</span>{" "}
                    immediately. They'll lose access to AI features until they
                    resubscribe (or you gift them another tier).
                </p>
            </ActionDialog>
        </section>
    );
}

// ----- iter-96: Free-tier admin card -----

